import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { listMetrics } from '../../services/listMetricsService';
import MetricsList from './MetricsList';
import MetricsCartModal from './MetricsCartModal';
import { detectLanguagesFromSegments } from '../../utils/languageDetect';
// PAYMENT COMMENTED OUT
// import TwoCheckoutPayment from '../payment/TwoCheckoutPayment';

const MetricsOptionsPanel = ({
  onEvaluate,
  isEvaluating,
  evaluationError,
  totalCharacters,
  totalSegments = 0,
  segments = [],
  metricId,
  onEnsureMetricId,
  onCreateMetric,
  onUpdateMetric,
  onAddMetricItems,
  onProcessMetric,
  getEvaluationName,
  metricExists = false
}) => {
  const [metricList, setMetricList] = useState([]);
  const [allMetricsData, setAllMetricsData] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [optionsError, setOptionsError] = useState('');
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    standard: true,
    bertscore: false,
    comet: false,
    metricx: false
  });
  // PAYMENT COMMENTED OUT
  // const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  // const [paymentData, setPaymentData] = useState(null); // { totalPrice, isPromocodeApplied }
  
  
  useEffect(() => {
    const loadMetricsOptions = async () => {
      try {
        setIsLoadingOptions(true);
        setOptionsError('');
        const response = await listMetrics();

        const isStandardMetric = (id = '', name = '') => {
          const idLower = id.toLowerCase();
          const nameLower = name.toLowerCase();
          if (idLower.includes('bert') || nameLower.includes('bertscore') || nameLower.includes('bert')) {
            return false;
          }
          if (idLower.includes('comet') || nameLower.includes('comet')) {
            return false;
          }
          if (idLower.includes('metric') || nameLower.includes('metricx') || nameLower.includes('metric')) {
            return false;
          }
          return true;
        };

        const transformedOptions = (response?.metrics && Array.isArray(response.metrics)) 
        ? response.metrics.map((metric) => {
            const key = metric.id?.toLowerCase() || '';
            const isStandard = isStandardMetric(metric.id || '', metric.name || '');
            return {
              key,
              name: metric.name || '',
              description: metric.description,
              io: metric.io,
              enabled: true,
              selected: isStandard,
              pricePerHour: metric?.pricePerHourUsd ?? 0,
            };
          })
        : [];

        setMetricList(transformedOptions);
        setAllMetricsData(response?.metrics || []);
      } catch (error) {
        console.error('Failed to load metrics options:', error);
        setOptionsError('Failed to load metrics options');
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadMetricsOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle metric toggle internally
  const handleMetricToggle = (metricKey) => {
    setMetricList(prev => prev.map(metric => 
      metric.key === metricKey 
        ? { ...metric, selected: !metric.selected }
        : metric
    ));
  };

  // Calculate price based on character count
  // const calculateSum = (characterCount) => {
  //   if (!characterCount || characterCount <= 0) return 0;
  //   
  //   const pricePer1K = 0.10; // $0.10 per 1K characters
  //   const basePrice = (characterCount / 1000) * pricePer1K;
  //   const finalPrice = Math.max(1.00, Math.min(100.00, basePrice)); // Min $1, Max $100
  //   
  //   return finalPrice;
  // };

  // PAYMENT COMMENTED OUT
  // const handlePaymentSuccess = async () => {
  //   setIsProcessingPayment(false);
  //   setPaymentData(null);
  //   
  //   const selectedMetrics = metricList.filter(m => m.selected).map(m => m.key);
  //   
  //   try {
  //     if (onProcessMetric) {
  //       await onProcessMetric();
  //     }
  //     
  //     if (onEvaluate) {
  //       onEvaluate(selectedMetrics);
  //     }
  //   } catch (error) {
  //     console.error('Error processing metrics after payment:', error);
  //   }
  // };

  // const handlePaymentClose = () => {
  //   console.log('Payment cancelled or closed without payment');
  //   setIsProcessingPayment(false);
  //   setPaymentData(null);
  //   setIsCartModalOpen(true);
  // };

  const handleCheckout = async (email /* PAYMENT COMMENTED OUT: totalPrice, isPromocodeApplied */) => {
    const selectedMetrics = metricList.filter(m => m.selected).map(m => m.key);
    
    /* PAYMENT COMMENTED OUT - original payment-gated logic:
    if (isPromocodeApplied) {
      setIsCartModalOpen(false);
      
      try {
        if (onProcessMetric && selectedMetrics.length > 0) {
          await onProcessMetric(selectedMetrics);
        }
        
        if (onEvaluate) {
          onEvaluate(selectedMetrics);
        }
      } catch (error) {
        console.error('Error processing metrics after promocode:', error);
      }
    } else {
      setIsCartModalOpen(false);
      setTimeout(() => {
        setIsProcessingPayment(true);
        setPaymentData({ totalPrice, isPromocodeApplied });
      }, 100);
    }
    */

    const ensuredMetricId = metricId || (onEnsureMetricId ? onEnsureMetricId() : null);
    setIsCartModalOpen(false);
    if (!ensuredMetricId || selectedMetrics.length === 0) {
      return;
    }

    if (onEvaluate) {
      onEvaluate(selectedMetrics);
    }

    try {

      if (!metricExists && onCreateMetric) {
        await onCreateMetric(selectedMetrics, email, ensuredMetricId);
      }

      const evaluationName = getEvaluationName ? getEvaluationName() : 'Untitled Metrics Run';

      if (onUpdateMetric) {
        if (!getEvaluationName()) {
          await onUpdateMetric('name', evaluationName, ensuredMetricId);
        }
        // if (email) {
        //   await onUpdateMetric('email', email, ensuredMetricId);
        // }
        const detected = detectLanguagesFromSegments(segments);
        const sourceLang = detected.source || 'detect';
        const targetLang = detected.target || 'detect';
        await onUpdateMetric('sourceLanguage', sourceLang, ensuredMetricId);
        await onUpdateMetric('targetLanguage', targetLang, ensuredMetricId);
        await onUpdateMetric('metrics', {metrics: selectedMetrics}, ensuredMetricId);
      }

      if (onAddMetricItems) {
        await onAddMetricItems(selectedMetrics, ensuredMetricId);
      }

      if (onProcessMetric) {
        await onProcessMetric(ensuredMetricId);
      }

    } catch (error) {
      console.error('Error processing metrics:', error);
    }
  };

  const hasValidSegments = () => {
    if (!segments || segments.length === 0) {
      return false;
    }
    
    return segments.some(segment => {
      const hasTarget = segment.target && segment.target.trim().length > 0;
      const hasSource = segment.source && segment.source.trim().length > 0;
      const hasReference = segment.reference && segment.reference.trim().length > 0;
      
      return hasTarget && (hasSource || hasReference);
    });
  };

  const handleEvaluateClick = async () => {
    if (!hasValidSegments()) {
      return;
    }
    
    setIsCartModalOpen(true);
  };

  return (
    <div className="space-y-6">

      {/* Metrics Selection */}
      <Card className="w-full max-w-full">
        <CardContent className="p-4">
          <h2 className="text-base font-medium text-[#020817] mb-1">Select Metrics</h2>
          <MetricsList
            metricList={metricList}
            onMetricToggle={handleMetricToggle}
            error={optionsError}
            onExpandedCategoriesChange={setExpandedCategories}
          />
          <div className="mt-6">
          <Button
            onClick={handleEvaluateClick}
            disabled={isEvaluating || isLoadingOptions || !hasValidSegments()}
            className="w-full h-10 bg-[#020817] hover:bg-[rgb(17,24,39)]/90 
            text-base text-white shadow-[0px_1px_3px_0px_#0000001A] 
            disabled:bg-[#E2E8F0] disabled:text-[#9CA3AF] disabled:cursor-not-allowed"
          >
            {isEvaluating ? 'Running...' : 'Run Metrics'}
          </Button>
          {evaluationError && (
            <div className="mt-2 text-sm text-red-500 text-center">{evaluationError}</div>
          )}
        </div>
        </CardContent>
      </Card>

      {/* Metrics Cart Modal */}
      <MetricsCartModal
        isOpen={isCartModalOpen}
        onClose={() => setIsCartModalOpen(false)}
        metricList={metricList}
        onMetricToggle={handleMetricToggle}
        allMetricsData={allMetricsData}
        expandedCategoriesFromList={expandedCategories}
        totalSegments={totalSegments}
        totalCharacters={totalCharacters}
        segments={segments}
        onCheckout={handleCheckout}
      />

      {/* PAYMENT COMMENTED OUT - 2Checkout Payment
      {paymentData && (
        <TwoCheckoutPayment
          isOpen={isProcessingPayment}
          totalPrice={paymentData.totalPrice}
          isPromocodeApplied={paymentData.isPromocodeApplied}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentClose={handlePaymentClose}
        />
      )}
      */}

    </div>
  );
};

export default MetricsOptionsPanel;